/**
 * Os estabelecimentos SINTÉTICOS de teste de billing — fonte única da lista.
 *
 * Origem: #258 — 2026-08-11 15:52 +0200 (a lista nasceu em
 * `apps/functions/src/lib/billing/testModeTenants.ts`); movida para cá em
 * 2026-09-28 17:10 +0200 (#768).
 *
 * Por quê mora aqui: as CRM Functions (`apps/crm-functions`) também precisam
 * dela — um pagamento sandbox de um destes estabelecimentos ganha o lead e
 * registra a venda MARCADA como teste (#768) — e só resolvem
 * `@etiquetachef/types`, não `apps/functions`. Duas cópias da lista divergiriam
 * na primeira inclusão, e a divergência é exatamente o bug: o gateway vende em
 * sandbox para um tenant que o CRM não reconhece como teste, e a venda vira
 * comissão real.
 *
 * Contrato: lista fechada (`as const`); todos os ids são tenants sintéticos,
 * nenhum é cliente. Incluir um id exige aval do owner — muda o gateway e o
 * modo de venda do tenant (ver o cabeçalho completo em
 * `apps/functions/src/lib/billing/testModeTenants.ts`).
 *
 * Usado por: `isTestModeBillingTenant` (Functions: webhooks, venda, NFS-e) e
 * `isTestSaleTenant` (CRM Functions: `lib/appPayments.ts`). Os dois somam, só
 * no emulador, os ids da env {@link E2E_TEST_MODE_BILLING_TENANTS_ENV}.
 *
 * Issues: #258 (origem), #700 (E2E), #752 (`BR-88888888000191`), #768 (CRM),
 * #1005 (`BR-test-e2e`, id fixo do teste e2e quando os demais viraram autoId).
 */
export const TEST_MODE_BILLING_TENANT_IDS = [
  'IT-98765432103',
  'IT-00000000000',
  // Legado (#752): o tenant de teste e2e nascia `{CC}-{CNPJ}`. O documento foi
  // apagado em 2026-10-27 (#1006); o id fica para que webhook sandbox atrasado
  // e o reset do `scripts/e2e-asaas` (`LEGACY_TEST_TENANT_ID`) o tratem como teste.
  'BR-88888888000191',
  // #1005: id fixo do tenant de teste e2e desde que todo tenant novo é
  // `{CC}-{autoId}` — ver `TEST_E2E_TENANT_ID`.
  'BR-test-e2e',
] as const;

/**
 * Chave país+CNPJ (`crmLeadTaxKey`) do lead de teste do e2e de venda
 * (`scripts/e2e-sale`, `scripts/e2e-asaas`).
 *
 * Origem: #1005 — 2026-10-19 14:48 +0200 (o CNPJ sintético vem da #752).
 *
 * Por quê: todo tenant novo nasce `{CC}-{autoId}` (#1005), então o id do
 * tenant de teste deixou de ser previsível pelo CNPJ — e é o id que liga o
 * modo teste (gateway sandbox, venda marcada como teste). A venda deste CNPJ
 * é a ÚNICA que nasce com id fixo: {@link TEST_E2E_TENANT_ID}.
 *
 * Usado por: `runSaleProvision` (`provisionTenantForSale.ts`) e
 * `saleTenantIdFor` (`getSaleCheckoutOptions.ts`), em `apps/functions`.
 */
export const TEST_E2E_TAXKEY = 'BR-88888888000191';

/**
 * Id fixo do tenant que a venda do {@link TEST_E2E_TAXKEY} cria (decisão do
 * owner, #1005 — 2026-10-19 14:48 +0200). Está em
 * `TEST_MODE_BILLING_TENANT_IDS`. Usado por: `runSaleProvision`,
 * `saleTenantIdFor`, `scripts/e2e-sale`, `scripts/e2e-asaas`.
 */
export const TEST_E2E_TENANT_ID = 'BR-test-e2e';

/**
 * Env lida SÓ no emulador (`FUNCTIONS_EMULATOR === 'true'`): ids extras,
 * separados por vírgula, que o ponta a ponta da venda (`scripts/e2e-sale`)
 * injeta nos dois codebases. Origem: #700 — 2026-09-24 16:39 +0200.
 */
export const E2E_TEST_MODE_BILLING_TENANTS_ENV = 'E2E_TEST_MODE_BILLING_TENANT_IDS';

/**
 * Este id está na lista fechada? Sem a env do emulador — quem precisa dela
 * (Functions e CRM Functions) a soma no próprio runtime, porque `process` não
 * é assunto deste pacote (ele também roda no navegador).
 *
 * Origem: #768 — 2026-09-28 17:10 +0200. Usado por: `isTestModeBillingTenant`
 * (Functions) e `isTestSaleTenant` (CRM Functions).
 */
export function isListedTestModeTenant(tenantId: string): boolean {
  return (TEST_MODE_BILLING_TENANT_IDS as readonly string[]).includes(tenantId);
}
